// @flow

import * as React from "react";
import { withRouter } from "react-router-dom";
import { Form } from "react-final-form";

import Button from "../../components/Button";
import Card from "../../components/Card";
import FieldControl from "../../components/FieldControl";
import { FlashMessageConsumer } from "../../components/FlashMessages";
import FormRow from "../../components/FormRow";
import PageHeader from "../../components/PageHeader";
import PageLayout from "../../components/PageLayout";
import PasswordInput from "../../components/PasswordInput";

import { errors as e } from "../../utils/errors";
import { isFormSubmitting } from "../../utils/helpers";
import * as validators from "../../utils/validators";
import {
  REFRESH_PASSWORD_OLD_PASSWORD,
  REFRESH_PASSWORD_PASSWORD,
  REFRESH_PASSWORD_PASSWORD_CONFIRMATION
} from "./formFields";

type RefreshPasswordProps = {
  location: {
    search: string,
    state: ?{
      from: ?string
    }
  },
  history: {
    push: ({ pathname: string, search?: string }) => void
  },
  passwordRegex: RegExp,
  passwordError: string,
  onSubmit: (
    values: {
      old_password: string,
      password: string,
      password_confirmation: string
    },
    handleRedirect: () => void,
    mapFormErrors: (any) => void,
    showFlashMessage: (string) => void
  ) => void | Promise<any>,
  defaultRedirectPath: string
};

type RefreshPasswordState = {|
  isOldPasswordErrorShowed: boolean,
  isPasswordErrorShowed: boolean
|};

class RefreshPassword extends React.Component<RefreshPasswordProps, RefreshPasswordState> {
  state = {
    isOldPasswordErrorShowed: false,
    isPasswordErrorShowed: false
  };

  redirect = () => {
    const { location, history, defaultRedirectPath } = this.props;

    const sessionPersistedUser = sessionStorage.getItem("persist:user");
    if (sessionPersistedUser) {
      const parsedSessionUser = JSON.parse(sessionPersistedUser);
      const sessionReferrer = parsedSessionUser.referrer
        ? JSON.parse(parsedSessionUser.referrer)
        : false;

      if (sessionReferrer) {
        parsedSessionUser.referrer = undefined;

        sessionStorage.removeItem("persist:user");

        window.location.href = decodeURIComponent(sessionReferrer);

        return;
      }
    }

    if (!history) {
      return;
    }

    const from = location?.state?.from;

    history.push({
      pathname: from || defaultRedirectPath,
      search: (from && location.search) || ""
    });
  };

  mapFormErrors = (errorResponse: { data: { errors?: { type: string }[] } }) => {
    const { errors } = errorResponse.data;
    if (!errors) {
      return;
    }

    errors.forEach(error => {
      switch (error.type) {
        case "invalid_password":
          this.setState({ isOldPasswordErrorShowed: true });
          break;
        case "password_already_used":
          this.setState({ isPasswordErrorShowed: true });
          break;
        default:
          break;
      }
    });
  };

  submitForm = (onSubmit, showFlashMessage) => values => {
    this.setState({ isOldPasswordErrorShowed: false, isPasswordErrorShowed: false });

    return onSubmit(values, this.redirect, this.mapFormErrors, showFlashMessage);
  };

  render() {
    const { passwordRegex, passwordError, onSubmit } = this.props;
    const { isOldPasswordErrorShowed, isPasswordErrorShowed } = this.state;

    return (
      <PageLayout>
        <PageHeader header="Neues Passwort festlegen" bottomMargin />
        <Card>
          <header>
            Ihr Passwort ist abgelaufen. Bitte geben Sie Ihr altes Passwort an und vergeben Sie
            anschließend ein neues.
          </header>
          <FlashMessageConsumer>
            {({ addMessage }) => {
              const showFlashMessage = msg => addMessage(msg, "failure");

              return (
                <Form onSubmit={this.submitForm(onSubmit, showFlashMessage)}>
                  {({ form }) => (
                    <form onSubmit={() => false}>
                      <FormRow>
                        <FieldControl
                          name={REFRESH_PASSWORD_OLD_PASSWORD}
                          caption={() => "Vorheriges Passwort"}
                          validate={validators.required(e.requiredField())}
                          validationMessage={isOldPasswordErrorShowed ? e.invalidPassword() : null}
                        >
                          {({ input, meta }) => (
                            <PasswordInput
                              input={input}
                              meta={meta}
                              placeholder="Geben Sie Ihr Vorheriges Passwort an..."
                            />
                          )}
                        </FieldControl>
                        <FieldControl
                          name={REFRESH_PASSWORD_PASSWORD}
                          caption={() => "Neues Passwort"}
                          validate={validators.combineValidators(
                            validators.required(e.requiredField()),
                            validators.isPassword(passwordRegex, passwordError)
                          )}
                          validationMessage={
                            isPasswordErrorShowed ? e.isAlreadyUsedPassword() : null
                          }
                        >
                          {({ input }) => (
                            <PasswordInput
                              input={input}
                              placeholder="Geben Sie Ihr Neues Passwort an..."
                            />
                          )}
                        </FieldControl>
                        <FieldControl
                          name={REFRESH_PASSWORD_PASSWORD_CONFIRMATION}
                          caption={() => "Neues Passwort Wiederholen"}
                          validate={validators.combineValidators(
                            validators.required(e.requiredField()),
                            validators.equalToField(REFRESH_PASSWORD_PASSWORD, e.passwordMatch())
                          )}
                        >
                          {({ input }) => (
                            <PasswordInput
                              input={input}
                              placeholder="Geben Sie erneut Ihr Neues Passwort an..."
                            />
                          )}
                        </FieldControl>
                      </FormRow>
                      <Button
                        data-testid="password-refresh-button"
                        isDisabled={!form.getState().valid || isFormSubmitting(form)}
                        onClick={form.submit}
                      >
                        Passwort festlegen
                      </Button>
                    </form>
                  )}
                </Form>
              );
            }}
          </FlashMessageConsumer>
        </Card>
      </PageLayout>
    );
  }
}

export default withRouter(RefreshPassword);
